import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { MainStore, Region, SnapMode } from "../../../store";
import { seconds_to_pixels } from "../../../util/time_position_conversion";
import { getZoomProportion } from "../../../util/zoom_levels";
import { Container } from "./styles";
import {
  bpm_to_seconds_per_measure,
  bpm_to_spb
} from "../../../util/quantization";
import { DraggableCore, DraggableData, DraggableEvent } from "react-draggable";
import {
  SET_IN_MARKER_SONG_TIME,
  SET_OUT_MARKER_SONG_TIME,
  SetInMarkerSongTimeAction,
  SetOutMarkerSongTimeAction
} from "../../../actions/MarkerTypes";
import {
  EditorContext,
  EditorState
} from "../../../context/EditorContext/EditorProvider";

export const MARKER_PIXEL_WIDTH = 20;

export let InOutMarkers = () => {
  const editor = useContext(EditorContext);
  const dispatch = useDispatch();

  const zoomLevel = useSelector<MainStore, number>(
    state => state.uiState.zoomLevel
  );
  const snapMode = useSelector<MainStore, SnapMode>(
    state => state.uiState.snap
  );
  const songLength = useSelector<MainStore, number>(
    state => state.project.customMixInfo.length
  );
  const previousSongLength = useRef(songLength);
  const regions = useSelector<MainStore, Region[]>(state =>
    Object.values(state.project.regions.byId)
  );
  const bpm = useSelector<MainStore, number>(
    state => state.project.initialTrackData.bpm
  );
  const beatsPerMeasure = useSelector<MainStore, number>(
    state => state.project.initialTrackData.beatsPerMeasure
  );
  const initialBeat = useSelector<MainStore, number>(
    state => state.project.initialTrackData.initialBeat
  );
  const inMarkerTime = useSelector<MainStore, number>(
    state => state.project.markers.in.songTime
  );
  const outMarkerTime = useSelector<MainStore, number>(
    state => state.project.markers.out.songTime
  );
  const mouseTime = useSelector<MainStore, number>(
    state => state.editorState.mouseTime
  );

  const [inMarkerState, setInMarkerState] = useState({
    isDragging: false,
    songTime: inMarkerTime
  });
  const [outMarkerState, setOutMarkerState] = useState({
    isDragging: false,
    songTime: outMarkerTime
  });

  // this allow wet fx to settle at the end to not cut off
  const OUT_SLOP_TIME_S = 2;

  const setAndSaveMarker = (
    markerType: string,
    songTime: number,
    snap: boolean = false
  ) => {
    if (markerType === "in") {
      let finalSongTime = Math.min(
        songTime,
        outMarkerState.songTime ?? songLength
      );
      if (snap) {
        finalSongTime = snapMarkerTime(finalSongTime);
      }
      setInMarkerState({ isDragging: false, songTime: finalSongTime });
      dispatch<SetInMarkerSongTimeAction>({
        type: SET_IN_MARKER_SONG_TIME,
        songTime: finalSongTime
      });
    } else {
      let finalSongTime = Math.max(songTime, inMarkerState.songTime ?? 0);
      if (snap) {
        finalSongTime = snapMarkerTime(finalSongTime);
      }
      setOutMarkerState({ isDragging: false, songTime: finalSongTime });
      dispatch<SetOutMarkerSongTimeAction>({
        type: SET_OUT_MARKER_SONG_TIME,
        songTime: finalSongTime
      });
    }
  };

  // sync local state to redux state
  // if no in/out markers pinned, set defaults to project bounds
  useEffect(() => {
    if (
      editor.state === EditorState.REHYDRATED ||
      editor.state === EditorState.LOADED
    ) {
      if (inMarkerTime !== null && outMarkerTime !== null) {
        setInMarkerState({ isDragging: false, songTime: inMarkerTime });
        setOutMarkerState({ isDragging: false, songTime: outMarkerTime });
      } else {
        const { exportStartTime, exportEndTime } = calculateDefaultMixBounds();
        setAndSaveMarker("in", exportStartTime, false);
        setAndSaveMarker("out", exportEndTime, false);
      }
    } else if (
      editor.state === EditorState.LOADING ||
      editor.state === EditorState.CLOSED
    ) {
      setInMarkerState({ isDragging: false, songTime: null });
      setOutMarkerState({ isDragging: false, songTime: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor.state, inMarkerTime, outMarkerTime]);

  // sync loaded song length after rehydration
  useEffect(() => {
    if (editor.state !== EditorState.LOADED) {
      previousSongLength.current = songLength;
    }
  }, [editor.state, previousSongLength, songLength]);

  // handle when mix extends beyond out marker (from user moving region)
  // extend out marker to accomodate
  useEffect(() => {
    if (
      editor.state === EditorState.LOADED &&
      previousSongLength.current !== songLength
    ) {
      if (
        !(inMarkerState.isDragging || outMarkerState.isDragging) &&
        outMarkerState.songTime < songLength
      ) {
        const newOutMarkerTime = songLength + OUT_SLOP_TIME_S;
        setAndSaveMarker("out", newOutMarkerTime, true);
      }
      previousSongLength.current = songLength;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor.state, songLength, inMarkerState, outMarkerState]);

  const secondsPerBeat = bpm_to_spb(bpm);
  const secondsPerMeasure = bpm_to_seconds_per_measure(bpm, beatsPerMeasure);

  function snapMarkerTime(songTime: number) {
    let snappedTime = null;
    switch (snapMode) {
      case SnapMode.BAR:
        snappedTime =
          Math.round(songTime / secondsPerMeasure) * secondsPerMeasure;
        break;
      case SnapMode.BEAT:
        snappedTime = Math.round(songTime / secondsPerBeat) * secondsPerBeat;
        break;
      default:
        snappedTime = songTime;
    }

    return snappedTime;
  }

  const handleMarkerDrag = (e: DraggableEvent, data: DraggableData) => {
    let newTime = snapMarkerTime(
      data.x / (seconds_to_pixels(1) * getZoomProportion(zoomLevel))
    );
    const isInMarker = data.node.id === "in-marker";
    const stateToUpdate = isInMarker ? setInMarkerState : setOutMarkerState;
    // perform marker bounds check
    if (isInMarker) {
      newTime = Math.min(newTime, outMarkerState.songTime);
    } else {
      newTime = Math.max(newTime, inMarkerState.songTime);
    }

    stateToUpdate({
      isDragging: true,
      songTime: newTime
    });
  };

  const handleMarkerDragStop = (e: DraggableEvent, data: DraggableData) => {
    if (data.node.id === "in-marker") {
      setAndSaveMarker("in", inMarkerState.songTime, true);
    } else {
      setAndSaveMarker("out", outMarkerState.songTime, true);
    }
  };

  /*
   *  Calculates intelligent start and stop time render markers for the mix.
   *  Set export bounds by defaulting to initial track offset so everything's aligned
   *  ... unless there would be more than a bar of silence at the beginning
   *  (meaning the user has moved regions around and deviated from the initial track
   *  in a significant way), in which case we set the start export time to 500ms before
   *  their first region.
   *
   *  Returns:
   *    startTimeSeconds (number): time to begin render (start locater position)
   *    endTimeSeconds (number): time to end render (end locater position)
   */
  const calculateDefaultMixBounds = () => {
    // calculate min audible region start and max region end
    let minAudibleRegionStartTime = songLength;
    let maxAudibleRegionEndTime = 0;
    regions.forEach(region => {
      // update min/max if region exceeds current bounds
      if (region.visualStartTime < minAudibleRegionStartTime) {
        minAudibleRegionStartTime = region.visualStartTime;
      }
      const regionEndTime = region.visualStartTime + region.visualDuration;
      if (regionEndTime > maxAudibleRegionEndTime) {
        maxAudibleRegionEndTime = regionEndTime;
      }
    });

    // if first audible region is <= 1 bar of time then set start bound to initialBeat time
    // (if initialBeat set properly, this will keep export beat synced in external DAW).
    // Otherwise, or if free time (no bpm), set audible region to 500ms before first
    // audible region starts
    let exportStartTime = (beatsPerMeasure - initialBeat) * secondsPerBeat;
    if (bpm in [0, 1] || minAudibleRegionStartTime > secondsPerMeasure) {
      exportStartTime = Math.max(0, minAudibleRegionStartTime);
    }
    const exportEndTime = maxAudibleRegionEndTime;
    exportStartTime = Math.min(exportStartTime, exportEndTime);

    return {
      exportStartTime,
      exportEndTime
    };
  };

  const handleKeyDown = useCallback(
    e => {
      if (["text", "number", "textarea"].includes(e.target.type)) return;

      if (e.keyCode === 73) {
        // i: set in marker position
        setAndSaveMarker("in", mouseTime, true);
      } else if (e.keyCode === 79) {
        //o: set out marker position
        setAndSaveMarker("out", mouseTime, true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mouseTime]
  );

  // create a mutable ref to handler that gets refreshed with up-to-date redux store every rerender
  // this is necessary because of useEffect dependencies & redux
  const handleKeyDownRef = useRef(handleKeyDown);
  useEffect(() => {
    handleKeyDownRef.current = handleKeyDown;
  }, [handleKeyDown]);

  useEffect(() => {
    const keyDownHandler = e => handleKeyDownRef.current(e);
    if (editor.drawerState.visible) {
      document.addEventListener("keydown", keyDownHandler);
    } else {
      document.removeEventListener("keydown", keyDownHandler);
    }
    return () => document.removeEventListener("keydown", keyDownHandler);
  }, [editor.drawerState.visible]);

  if (inMarkerState.songTime !== null && outMarkerState.songTime !== null) {
    return (
      <Container id="in-out-markers">
        <DraggableCore onDrag={handleMarkerDrag} onStop={handleMarkerDragStop}>
          <div
            id="in-marker"
            className="marker-triangle"
            style={{
              left:
                seconds_to_pixels(inMarkerState.songTime) *
                getZoomProportion(zoomLevel)
            }}
          ></div>
        </DraggableCore>
        <DraggableCore onDrag={handleMarkerDrag} onStop={handleMarkerDragStop}>
          <div
            id="out-marker"
            className="marker-triangle"
            style={{
              left:
                seconds_to_pixels(outMarkerState.songTime) *
                  getZoomProportion(zoomLevel) -
                MARKER_PIXEL_WIDTH
            }}
          ></div>
        </DraggableCore>
      </Container>
    );
  }
  return <></>;
};
